// File: /opt/dev_environment/assets/workspace/app-init/src/services/pronunciationApi.js

/**
 * Pronunciation Audio API service.
 * Calls the backend to get MP3 pronunciation audio and example sentence
 * for a given vocabulary card.
 * Now includes Bearer token authentication.
 */

import AuthService from './authService';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const ENDPOINT = `${API_BASE}/api/v1/pronunciation-audio`;

// In-memory cache to avoid re-fetching the same word pair
const cache = new Map();

function getCacheKey(germanWord, italianWord) {
  return `${germanWord}|||${italianWord}`;
}

/**
 * Get a valid access token, refreshing if necessary.
 */
async function getValidAccessToken() {
  let accessToken = AuthService.getAccessToken();

  if (!accessToken) {
    const refreshToken = AuthService.getRefreshToken();
    if (refreshToken) {
      try {
        accessToken = await AuthService.refreshAccessToken();
      } catch {
        return null;
      }
    }
  }

  return accessToken;
}

/**
 * Fetch pronunciation audio and example sentence for a word pair.
 * Returns { audioUrl, word, exampleSentence } or throws on error.
 *
 * The audioUrl is an object URL that must be revoked after use.
 */
export async function fetchPronunciation(germanWord, italianWord) {
  const key = getCacheKey(germanWord, italianWord);

  // Return cached result if available
  if (cache.has(key)) {
    const cached = cache.get(key);
    // Re-create object URL from cached blob
    const audioUrl = URL.createObjectURL(cached.blob);
    return {
      audioUrl,
      word: cached.word,
      exampleSentence: cached.exampleSentence,
    };
  }

  // Get auth token
  const accessToken = await getValidAccessToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      german_word: germanWord,
      italian_word: italianWord,
    }),
  });

  if (!response.ok) {
    throw new Error(`Pronunciation API error: ${response.status} ${response.statusText}`);
  }

  // Read metadata from custom headers
  const word = response.headers.get('X-Word') || italianWord;
  const exampleSentence = response.headers.get('X-Example-Sentence') || '';

  // Read audio blob
  const blob = await response.blob();
  const audioUrl = URL.createObjectURL(blob);

  // Cache the blob and metadata
  cache.set(key, { blob, word, exampleSentence });

  return { audioUrl, word, exampleSentence };
}

/**
 * Fetch pronunciation for a verb card.
 * Uses the conjugated Italian form and German conjugation.
 */
export async function fetchVerbPronunciation(card) {
  const germanWord = card.germanConjugation || card.germanInfinitive || '';
  const italianWord = card.conjugatedAnswer || card.baseVerb || '';
  return fetchPronunciation(germanWord, italianWord);
}

/**
 * Clear the pronunciation cache (e.g. on data reset).
 */
export function clearPronunciationCache() {
  cache.clear();
}